import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { safeStorage } from '../utils/safeStorage';

const PreferencesContext = createContext(null);
const STORAGE_KEY = 'polkdesq_preferences';

const DEFAULT_PREFERENCES = {
  commandBarEnabled: true,
  coachHintsEnabled: true,
  focusBlockMinutes: 50,
  reviewModeEnabled: true,
  defaultNewTaskDestination: 'today',   // 'today' | 'inbox'
  densityMode: 'compact',               // 'compact' | 'comfortable' | 'spacious'
  attentionBudgets: {},   // { businessId: weeklyHours }
  energyCurve: {          // 3-zone energy model
    peak: { start: '09:00', end: '12:00' },
    recovery: { start: '13:00', end: '14:00' },
    steady: { start: '14:00', end: '18:00' },
  },
};

const VALID_DENSITY_MODES = new Set(['compact', 'comfortable', 'spacious']);

export function PreferencesProvider({ children }) {
  const [preferences, setPreferences] = useState(() => {
    const stored = safeStorage.getJSON(STORAGE_KEY, {});
    return { ...DEFAULT_PREFERENCES, ...(stored && typeof stored === 'object' ? stored : {}) };
  });

  useEffect(() => {
    safeStorage.setJSON(STORAGE_KEY, preferences);
  }, [preferences]);

  // Reflect density mode on <html data-density="..."> so CSS variables can
  // scale spacing globally without component changes.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const mode = VALID_DENSITY_MODES.has(preferences.densityMode)
      ? preferences.densityMode
      : 'compact';
    document.documentElement.setAttribute('data-density', mode);
  }, [preferences.densityMode]);

  const value = useMemo(() => ({
    preferences,
    setPreference: (key, value) => setPreferences((prev) => ({ ...prev, [key]: value })),
    patchPreferences: (updates) => setPreferences((prev) => ({ ...prev, ...updates })),
    resetPreferences: () => setPreferences(DEFAULT_PREFERENCES),
  }), [preferences]);

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (!context) throw new Error('usePreferences must be used within PreferencesProvider');
  return context;
}
